import { useCallback, useEffect, useReducer, useRef } from 'react';
import { validateCart } from 'api/checkout';
import { DEMO_CATALOG } from 'api/catalog';
import { useCart } from 'context/CartContext';
import {
  initialValidation,
  summarizeValidation,
  validationKey,
  validationReducer,
} from './cartValidation';

export const VALIDATION_DEBOUNCE_MS = 400;

/**
 * Ask the server what the cart is worth right now: prices, stock, the coupon
 * and, with a chosen delivery option, the shipping and the total.
 *
 *   const { status, data, error, refresh } = useCartValidation({ enabled, shipping });
 *   data: summarizeValidation(...)   (see cartValidation.js)
 *
 * It asks again, after a short pause, whenever the cart, the coupon or the
 * shipping choice changes; answers to an older question are dropped. Without
 * lines, when disabled, or on the demo catalog (no server), it stays idle.
 * `coupon` overrides the cart's coupon (to try a code before applying it).
 */
export default function useCartValidation({ enabled = true, shipping = null, coupon } = {}) {
  const { lines, couponCode } = useCart();
  const [state, dispatch] = useReducer(validationReducer, initialValidation);
  const effectiveCoupon = coupon === undefined ? couponCode : coupon;
  const active = enabled && !DEMO_CATALOG && lines.length > 0;
  const key = validationKey({ lines, couponCode: effectiveCoupon, shipping });
  const latest = useRef({ lines, couponCode: effectiveCoupon, shipping });
  latest.current = { lines, couponCode: effectiveCoupon, shipping };
  const requestId = useRef(0);
  const controller = useRef(null);

  const run = useCallback(async () => {
    const mine = (requestId.current += 1);
    if (controller.current) controller.current.abort();
    controller.current = new AbortController();
    dispatch({ type: 'start' });
    const asked = latest.current;
    try {
      const response = await validateCart(asked, { signal: controller.current.signal });
      if (mine === requestId.current) dispatch({ type: 'success', summary: summarizeValidation(asked.lines, response), key: validationKey(asked) });
    } catch (error) {
      if (error.code === 'REQUEST_CANCELED') return;
      if (mine === requestId.current) dispatch({ type: 'failure', error });
    }
  }, []);

  useEffect(() => {
    if (!active) {
      requestId.current += 1;
      if (controller.current) controller.current.abort();
      dispatch({ type: 'reset' });
      return undefined;
    }
    const timer = window.setTimeout(run, VALIDATION_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [active, key, run]);

  useEffect(
    () => () => {
      requestId.current += 1;
      if (controller.current) controller.current.abort();
    },
    []
  );

  const refresh = useCallback(() => run(), [run]);
  // `current`: the answer is about the cart, coupon and shipping as they are now.
  return { ...state, active, current: state.status === 'ready' && state.key === key, refresh };
}
